import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  orderStatus,
  orderStatusDescription,
} from "../../constants/commonConstants";
import showToast from "../../utils/toastNotifications";

import ProgressIndicator from "../../components/Common/ProgressIndicator";
import CustomButton from "../../components/Form/CustomButton";
import DeliveryDetails from "../../components/Client/OrderDetail/DeliveryDetailsCard";
import OfferDetails from "../../components/Client/OrderDetail/OfferDetailsCard";
import RevisionDetails from "../../components/Client/OrderDetail/RevisionDetailsCard";
import IssueDetails from "../../components/Client/OrderDetail/IssueDetailsCard";
import ReviewOrderModel from "../../components/Model/ReviewOrderModel";
import DeliverJobModel from "../../components/Model/DeliverJobModel";
import RaiseIssueModel from "../../components/Model/RaiseIssueModel";
import { getUserOrderById, addOrderReview } from "../../store";
import { useThunk } from "../../hooks/useThunk";
import ResolveDetails from "../../components/Client/OrderDetail/ResolveDetailsCard";
import { overRideStatus } from "../../utils/statusUtils";
import { currencyFormatter } from "../../utils/formatting";
import { formatYYYY_MM_DD } from "../../utils/dateFormating";
import ErrorDisplay from "../../error/ErrorDisplay";
import { isValidObjectId } from "../../utils/validationUtils";
import OrderDetailSkeleton from "../../components/skeleton/OrderDetailSkeleton";

const TaskerOrderDetails = () => {
  const navigate = useNavigate();

  const { orderId } = useParams();
  const jobId = orderId;

  const [isOpenDeliverJobModel, setIsOpenDeliverJobModel] = useState(false);
  const [isOpenReviewOrderModel, setIsOpenReviewOrderModel] = useState(false);
  const [isOpenRaiseIssueModel, setIsOpenRaiseIssueModel] = useState(false);

  // Note: fetch user orders
  const [doGetUserOrderByID, isGetUserOrderByID, getUserOrderByIDError] =
    useThunk(getUserOrderById);
  const [doAddOrderReview, isAddOrderReview, addOrderReviewError] =
    useThunk(addOrderReview);

  const [orderData, setOrderData] = useState();

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const authData = JSON.parse(localStorage.getItem("auth") || null);

  // fetch orders by orderId
  const fetchOrder = async () => {
    const userOrder = await doGetUserOrderByID(jobId);

    const data = userOrder?.response?.data;

    if (userOrder?.success) {
      setOrderData(data);
    }
  };

  const checkStatus = (status) => {
    return orderData?.orderProgress?.some(
      (item) => item.progressStatus === status
    );
  };

  const handleOrderReviewSubmit = async (data) => {
    const payload = {
      orderId: jobId,
      rating: data.rating,
      feedback: data.feedBack,
    };
    const reviewedOrder = await doAddOrderReview(payload);

    if (reviewedOrder?.success) {
      showToast("success", "Review Added Successfully");
      setIsOpenReviewOrderModel(false);
      fetchOrder();
    } else {
      showToast("error", reviewedOrder?.error?.message || "Failed to Add Review");
      setIsOpenReviewOrderModel(false);
    }

  };

  return (
    <div className="mt-12 px-5 min-w-[320px] sm:px-10 lg:px-[6.25rem] mb-[5.5rem]">
      {
        // Show ErrorDisplay if orderId is invalid or there is an error
        !isValidObjectId(orderId) || getUserOrderByIDError ? (
          <ErrorDisplay
            errorMessage={`${
              getUserOrderByIDError?.message || "Invalid Order ID"
            }`}
            buttonText="Back to Order History"
            onButtonClick={() => navigate("/service-pro/order-history")}
          />
        ) : isGetUserOrderByID ? (
          // Show Skeleton while fetching data
          <OrderDetailSkeleton />
        ) : (
          <div className="flex flex-col lg:flex-row lg:gap-[2.875rem]">
            {/* Main Content */}
            <div className="flex flex-col flex-1 gap-3 h-fit">
              <div className="flex flex-col space-x-2 space-y-2 md:flex-row md:space-y-0 lg:space-x-0">
                {/* Top Card */}
                <div className="h-fit flex-1 p-4 sm:p-6 lg:p-9 bg-white rounded-[1.25rem] shadow-sm border border-light-blue">
                  {/* Header */}
                  <div className="flex flex-col gap-6 mb-10 ">
                    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:gap-0">
                      <div className="flex flex-col">
                        <h2 className="text-dark mb-0 font-nonito_sans font-semibold text-[1.5rem] leading-[34px] lg:leading-[2.97rem]">
                          {orderData?.title}
                        </h2>
                        {/* Description */}
                        <p className="text-content text-sm font-inter max-w-[90%] mb-0 mt-2 sm:max-w-full">
                          {
                            orderStatusDescription[
                              overRideStatus(orderData, authData?.role)
                            ]?.[authData?.role]
                          }
                        </p>
                      </div>
                      <span className="text-dark font-nonito_sans font-semibold text-[1.5rem] leading-[2.97rem]">
                        {currencyFormatter.format(orderData?.price)}
                      </span>
                    </div>
                    {/* Task Details */}
                    <div>
                      {/* Mobile layout */}
                      <div className="flex flex-col gap-2 sm:hidden">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-dark font-inter">
                            Order ID
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {`#${orderData?.id}`}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-dark font-inter">
                            Date
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {formatYYYY_MM_DD(orderData?.createdAt)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-dark font-inter">
                            Client Name
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {`${orderData?.user?.firstName} ${orderData?.user?.lastName}`}
                          </span>
                        </div>
                      </div>

                      {/* Desktop layout */}
                      <div className="hidden sm:flex gap-[2.125rem]">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-dark font-inter">
                            Order ID
                          </span>
                          <span className="text-sm text-dark font-inter">
                            Date
                          </span>
                          <span className="text-sm text-dark font-inter">
                            Client Name
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {`#${orderData?.id}`}
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {formatYYYY_MM_DD(orderData?.createdAt)}
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {`${orderData?.user?.firstName} ${orderData?.user?.lastName}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* In Progress or In Revision => Deliver Job */}
                  {(orderData?.status === orderStatus.ORDER_IN_PROGRESS ||
                    orderData?.status === orderStatus.IN_REVISION) && (
                    <div className="flex flex-col gap-4 ">
                      <CustomButton
                        onClick={() => setIsOpenDeliverJobModel(true)}
                        buttonText="Deliver Job"
                        className="w-full text-base font-bold bg-primary text-dark font-inter"
                      />
                    </div>
                  )}

                  {/* Completed => Add Review & Raise A Issue */}
                  {checkStatus(orderStatus.COMPLETED) && (
                    <div className="flex flex-col gap-4 ">
                      <CustomButton
                        type="button"
                        buttonText="Add Review"
                        onClick={() => {
                          if (!orderData?.userReview) {
                            setIsOpenReviewOrderModel(true);
                          } else {
                            showToast(
                              "error",
                              "User has already submitted a review"
                            );
                          }
                        }}
                        disabled={!!orderData?.userReview}
                        className="w-full text-base font-semibold bg-transparent border-dark text-primary font-inter"
                      />
                      <CustomButton
                        type="button"
                        onClick={() => {
                          if (!orderData?.issue) {
                            setIsOpenRaiseIssueModel(true);
                          } else {
                            showToast(
                              "error",
                              "User has already submitted a review"
                            );
                          }
                        }}
                        buttonText="Raise an Issue"
                        className="w-full text-base font-semibold bg-primary text-dark font-inter"
                        disabled={!!orderData?.issue}
                      />
                    </div>
                  )}
                </div>

                {/* Progress Indicator */}
                <div className="block mt-6 lg:mt-0 lg:hidden">
                  <ProgressIndicator
                    orderFlow={orderData?.orderProgress || []}
                    revitions={orderData?.revisions || []}
                    orderData={orderData}
                  />
                </div>
              </div>

              {/* Bottom Card */}
              <div className="px-4 sm:px-6 pt-6 pb-12 bg-white rounded-[1.25rem] border border-light-blue">
                <h2 className="text-2xl font-semibold font-nonito_sans text-dark mb-7">
                  Order Details
                </h2>
                <div className="flex flex-col gap-6 ">
                  {/* Offer Details appears in all statuses */}
                  {checkStatus(orderStatus.ORDER_IN_PROGRESS) && (
                    <OfferDetails
                      details={orderData}
                      showServiceProName={false}
                    />
                  )}

                  {/* if Delivered/Completed/InRevision/IssueRaised/Resolved => show DeliveryDetails */}
                  {checkStatus(orderStatus.ORDER_DELIVERED) && (
                    <DeliveryDetails details={orderData} />
                  )}

                  {/* if In Revision/Issue Raised/Resolved => show RevisionDetails */}
                  {/* if In Revision/Issue Raised/Resolved => show RevisionDetails */}
                  {checkStatus(orderStatus.IN_REVISION) &&
                    Array.isArray(orderData?.revisions) &&
                    orderData?.revisions.length > 0 &&
                    orderData?.revisions?.map((revision, index) => (
                      <React.Fragment key={revision._id || index}>
                        <RevisionDetails details={revision} />

                        {revision.status === "COMPLETED" && (
                          <DeliveryDetails details={revision} />
                        )}
                      </React.Fragment>
                    ))}

                  {/* if Resolved and had a second delivery after revision, show another DeliveryDetails */}
                  {/* {order.orderStatus === orderStatus.ISSUE_RESOLVED &&
                hasSecondDelivery && (
                  <DeliveryDetails details="New Delivery after revision" />
                )} */}

                  {/* if Issue Raised or Resolved => show IssueDetails */}
                  {checkStatus(orderStatus.ISSUE_RAISED) && (
                    <IssueDetails details={orderData?.issue} />
                  )}

                  {/* Resolved Details */}
                  {checkStatus(orderStatus.ISSUE_RESOLVED) && (
                    <ResolveDetails details={orderData?.issue} />
                  )}
                </div>
              </div>
            </div>
            {/* Progress Indicator */}
            <div className="hidden mt-6 lg:mt-0 lg:block">
              <ProgressIndicator
                orderFlow={orderData?.orderProgress || []}
                revitions={orderData?.revisions || []}
                orderData={orderData}
              />
            </div>
          </div>
        )
      }

      <DeliverJobModel
        isOpen={isOpenDeliverJobModel}
        order={orderData}
        setIsOpen={setIsOpenDeliverJobModel}
        fetchOrder={fetchOrder}
      />

      {/* Review Order Model */}
      <ReviewOrderModel
        isOpen={isOpenReviewOrderModel}
        setIsOpen={setIsOpenReviewOrderModel}
        onClose={() => setIsOpenReviewOrderModel(false)}
        onSubmit={handleOrderReviewSubmit}
        title="Review Order"
        loading={isAddOrderReview}
        fetchOrder={fetchOrder}
      />

      <RaiseIssueModel
        isOpen={isOpenRaiseIssueModel}
        setIsOpen={setIsOpenRaiseIssueModel}
        order={orderData}
        fetchOrder={fetchOrder}
      />
    </div>
  );
};

export default TaskerOrderDetails;
